import clsx from 'clsx';

import { darkGreen } from '@/theme/colors';

import ManageMembersButton from '../EditProjectMembers/ManageMembersButton';
import SettingsMenu from '../EditProjectMembers/SettingsMenu';
import MaterialIcon from '../Icon/MaterialIcon';
import Link from '../Link';
import MemberList, { type Member } from '../MemberList';
import s from './ProjectBar.module.scss';

/**
 * Only the fields this bar actually reads, rather than one caller's fragment: the ER page
 * passes `ProjectFieldsFragment` while the consultation page passes a narrower selection,
 * and both are legitimate.
 */
interface ProjectBarProject {
  id: string;
  name?: string | null;
  html_url: string;
  tracking_code?: string | null;
  group?: { name?: string | null } | null;
}

interface ProjectBarProps {
  // Read for `admin` here; otherwise forwarded to ManageMembersButton/SettingsMenu untouched.
  viewer: { admin?: boolean | null };
  // Required, and deliberately not optional: the bar is entirely about the project and
  // dereferences it on its first line. `ExpertRequest.project` is nullable in the schema
  // and optional on the Redux node, so callers must narrow before rendering. This prop was
  // `any` when Sentry WEBAPP-CDV / CDW / CDK were raised, which is why the compiler did not
  // flag the one caller that passed `undefined` straight into the destructure below.
  project: ProjectBarProject;
  showSettings?: boolean;
  manageMembers?: boolean;
  members?: Member[];
  className?: string;
}

function ProjectBar({
  viewer,
  project,
  showSettings,
  manageMembers,
  members,
  className,
}: ProjectBarProps): JSX.Element | null {
  // The prop is required, but the ER detail page reads its request from a Redux node typed
  // `Record<string, any>` and cast to `ExpertRequest`, so `any` can still carry an absent
  // project past the compiler — that is exactly how WEBAPP-CDV / CDW / CDK reached
  // production, as a whole-page RouteErrorElement. Rendering nothing costs a breadcrumb;
  // throwing here costs the page.
  if (!project) return null;

  const { group, tracking_code: trackingCode } = project;
  const groupName = viewer.admin && group ? group.name : '';

  let titleSufix;
  if (groupName && trackingCode) {
    titleSufix = ` (${groupName}: ${trackingCode})`;
  } else if (groupName) {
    titleSufix = ` (${groupName})`;
  } else if (trackingCode) {
    titleSufix = ` (${trackingCode})`;
  }

  return (
    <div className={clsx(s.bar, className)}>
      <div className={s.title}>
        <Link className={s.link} to={project.html_url}>
          <MaterialIcon color={darkGreen} className={s.icon} icon="arrow_back" />
          <span className={s.project}> Project:</span>
          {project.name}
          <span className={s.sufix}>{titleSufix}</span>
        </Link>
      </div>
      {}
      <div className={s.members}>
        {members && <MemberList members={members} />}
        {manageMembers && <ManageMembersButton viewer={viewer} project={project} />}
        {showSettings && <SettingsMenu project={project} />}
      </div>
    </div>
  );
}

export default ProjectBar;
